import { Injectable } from "@angular/core";

/** Step mínimo de comprimento em metros (10 cm). */
export const STEP_COMPRIMENTO_M = 0.1;

export interface CorteBasico {
  qtd: number;
  comprimento_util: number;
}

export interface CorteItem extends CorteBasico {
  id: string;
  flash?: boolean;
}

/** Evento de exclusão de uma linha da tabela de cortes. */
export interface CorteExcluirEvento {
  id: string;
  /** Quantidade da linha (quando menor que o item agrupado, remove só parte). */
  qtd: number;
  comprimento_util: number;
  barra: number | null;
}

/** Linha da tabela "Cortes adicionados" (agrupada por barra quando há `corte.barras`). */
export interface CorteLinhaGrid {
  barra: number | null;
  qtd: number;
  comprimento_util: number;
  /** `sobra_total` da barra — preenchido só na última linha do grupo. */
  sobra: number | null;
  itemId: string | null;
  flash?: boolean;
  exibirCelulaBarra: boolean;
  rowspanBarra: number;
  fimGrupoBarra: boolean;
}

export interface ProdutoCorteInfo {
  /** kg por metro (ou unidade_estoque por metro) */
  fatorConversao?: number | null;
  /** estoque retornado pelo backend (unidade base, ex.: Kg) */
  estoqueKg?: number | null;
  /** unidade de conversão para MT (estoque_mt = estoqueKg / unidadeConversao) */
  unidadeConversao?: number | null;
  /** comprimento da barra/rolo (m) */
  comprimentoProduto?: number | null;
  /** se permite vender fracionado (quando true, sobra é 0) */
  permiteFracionar?: boolean | null;
}

export interface ResumoCorte {
  totalCortes: number;
  /** Soma de todos os cortes (qtd × comprimento_util) */
  totalSolicitado: number;
  /** peso = totalFinal × fatorConversao (fallback local). */
  pesoTotal: number;
  /** Peso líquido da API (`peso_total_liquido`). */
  pesoTotalLiquido?: number;
  /** Preço unitário da API (`preco_unitario`). */
  precoUnitario?: number;
  /** Valor total: `preco_unitario × peso_total_liquido` (API ou calculado). */
  valorTotal?: number;
  /** Sobra total (`total_sobra` / `sobra_total` do GET). */
  sobraTotal: number;
  /** totalFinal = totalSolicitado + sobraTotal */
  totalFinal: number;
  /** Líquido elegível (`liquido_elegivel` do GET). */
  liquidoElegivel?: number;
  /** Líquido não elegível (`liquido_nao_elegivel` do GET). */
  liquidoNaoElegivel?: number;
  /** Sobra após devoluções: `total_sobra - (devolvido + devolvido_nao_elegivel)`. */
  sobraLiquida?: number;
  devolvido?: number;
}

/** Dados para o painel Controle de Devoluções. */
export interface CorteDevolucaoDados {
  sobraTotal: number;
  /** Sobra líquida (`sobra_liquida` = total_sobra − devoluções). */
  sobraLiquida: number;
  /** Disponível para devolver (`liquido_elegivel`). */
  sobraElegivel: number;
  /** Não elegível disponível (`liquido_nao_elegivel`). */
  sobraNaoElegivel: number;
  devolvido: number;
  devolvidoNaoElegivel: number;
  /** Múltiplo por unidade no stepper (`barras[].sobra_elegivel`). */
  multiploElegivel: number;
  /** Múltiplo por unidade no stepper (`barras[].sobra_nao_elegivel`). */
  multiploNaoElegivel: number;
}

/** Contexto para localizar o cálculo em `calculos_corte[]` (POST v2/pedido/itens). */
export interface CorteCalculoContexto {
  produtoId: number;
  cor?: number;
  estampa?: number;
  tamanho?: string;
  pedidoId?: number;
  pedidoClienteId?: number;
  clienteId?: number;
}

/** Barra retornada em GET produto/:id → `corte.barras[]`. */
export interface CorteBarraApi {
  cortes?: number[];
  numero?: number;
  status?: string;
  sobra_total?: number;
  tamanho_barra?: number;
  sobra_elegivel?: number;
  sobra_nao_elegivel?: number;
}

/** Bloco `corte` do detalhe do produto (novo contrato). */
export interface CorteBlocoApi {
  barras?: CorteBarraApi[];
  devolvido?: number;
  devolvido_nao_elegivel?: number;
  tipo_cobranca?: string;
  /** Valor total do serviço de corte (GET/POST produto ou `calculos_corte`). */
  valor_servico_corte?: number;
  /** Preço do serviço conforme tipo de cobrança (`preco_servico_corte`). */
  preco_servico_corte?: number;
  /** Preço unitário do produto com serviço (`preco_unitario`). */
  preco_unitario?: number;
  /** Peso líquido para valor total (`peso_total_liquido`). */
  peso_total_liquido?: number;
  /** Valor total do pedido de corte (`valor_total`), quando enviado pela API. */
  valor_total?: number;
  /** Metragem total considerada no cálculo de peso (`metragem_digitada`). */
  metragem_digitada?: number;
  total_sobra?: number;
  total_comprado?: number;
  liquido_elegivel?: number;
  liquido_nao_elegivel?: number;
  sobra_liquida?: number;
  /** @deprecated Use `liquido_elegivel`. */
  total_elegivel?: number;
  /** @deprecated Use `liquido_nao_elegivel`. */
  total_nao_elegivel?: number;
  /** Legado: lista plana de cortes. */
  cortes?: Array<{
    quantidade?: number;
    qtd?: number;
    comprimento_util?: number;
    comprimento?: number;
    tamanho?: number;
  }>;
}

/** Campos opcionais ao montar `detalhe` para POST v2/pedido/itens. */
export interface CorteDetalheSyncOpcoes {
  devolvido?: number;
  devolvido_nao_elegivel?: number;
  tipo_cobranca?: string;
}

@Injectable({ providedIn: "root" })
export class CorteService {
  novoId(): string {
    return typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  }

  formatarMetros(v: number): string {
    return v.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatarNumero(v: number): string {
    return this.formatarMetros(v);
  }

  /** Arredonda para 2 casas decimais (cálculos do resumo e valores monetários). */
  arredondar2(v: number): number {
    const n = Number(v);
    if (!Number.isFinite(n)) {
      return 0;
    }
    return Math.round(n * 100) / 100;
  }

  /** Arredonda para 3 casas decimais (peso líquido da API). */
  arredondar3(v: number): number {
    const n = Number(v);
    if (!Number.isFinite(n)) {
      return 0;
    }
    return Math.round(n * 1000) / 1000;
  }

  formatarPeso(v: number): string {
    const n = Number(v);
    if (!Number.isFinite(n)) {
      return this.formatarMetros(0);
    }
    return n.toLocaleString("pt-BR", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  }

  /** Múltiplo de 0,10 m com tolerância a float. */
  isMultiploDe10cm(v: number): boolean {
    if (!Number.isFinite(v)) {
      return false;
    }
    const escalado = Math.round(v * 100);
    return Math.abs(escalado - v * 100) < 1e-6 && escalado % 10 === 0;
  }

  calcularTotalMetros(itens: CorteBasico[]): number {
    const total = itens.reduce((s, i) => s + i.qtd * i.comprimento_util, 0);
    return this.arredondar2(total);
  }

  calcularPeso(totalMetros: number, fatorConversao: number | null | undefined): number {
    const fator = Number(fatorConversao ?? 0);
    if (!Number.isFinite(totalMetros) || !Number.isFinite(fator)) {
      return 0;
    }
    return this.arredondar2(totalMetros * fator);
  }

  /**
   * Metragem de referência para rateio de peso/valor (prioriza `metragem_digitada` da API).
   */
  extrairMetragemReferenciaCorte(bloco: CorteBlocoApi | null | undefined): number {
    if (!bloco) {
      return 0;
    }
    const digitada = Number(bloco.metragem_digitada);
    if (Number.isFinite(digitada) && digitada > 0) {
      return digitada;
    }
    const comprado = Number(bloco.total_comprado);
    const sobra = Number(bloco.total_sobra ?? bloco.sobra_liquida);
    let soma = 0;
    if (Number.isFinite(comprado) && comprado > 0) {
      soma += comprado;
    }
    if (Number.isFinite(sobra) && sobra > 0) {
      soma += sobra;
    }
    return soma > 0 ? this.arredondar2(soma) : 0;
  }

  /**
   * Peso (kg) para uma metragem: rateio de `peso_total_liquido` quando a API envia,
   * senão `metros × fator_conversao`.
   */
  calcularPesoMetrosCorte(
    metros: number,
    bloco?: CorteBlocoApi | null,
    fatorConversao?: number | null
  ): number {
    const m = Number(metros);
    if (!Number.isFinite(m) || m <= 0) {
      return 0;
    }
    const pesoLiquido = Number(bloco?.peso_total_liquido);
    const metragemRef = this.extrairMetragemReferenciaCorte(bloco);
    if (Number.isFinite(pesoLiquido) && pesoLiquido > 0 && metragemRef > 0) {
      return this.arredondar3((pesoLiquido * m) / metragemRef);
    }
    return this.calcularPeso(m, fatorConversao);
  }

  /** Valor em R$ = peso (kg) × `preco_unitario`. */
  calcularValorMetrosCorte(
    metros: number,
    fatorConversao: number | null | undefined,
    precoUnitario: number | null | undefined,
    bloco?: CorteBlocoApi | null
  ): number {
    const peso = this.calcularPesoMetrosCorte(metros, bloco, fatorConversao);
    const preco = Number(precoUnitario ?? 0);
    if (peso <= 0 || !Number.isFinite(preco) || preco <= 0) {
      return 0;
    }
    return this.arredondar2(peso * preco);
  }

  calcularSobra(
    totalMetros: number,
    comprimentoProduto: number | null | undefined,
    permiteFracionar: boolean | null | undefined
  ): number {
    if (permiteFracionar === true) {
      return 0;
    }
    const comp = Number(comprimentoProduto ?? 0);
    if (!Number.isFinite(totalMetros) || !Number.isFinite(comp) || comp <= 0) {
      return 0;
    }
    const resto = ((totalMetros % comp) + comp) % comp;
    if (resto < 1e-9 || Math.abs(comp - resto) < 1e-9) {
      return 0;
    }
    return this.arredondar2(comp - resto);
  }

  calcularResumo(itens: CorteBasico[], produto?: ProdutoCorteInfo | null): ResumoCorte {
    const totalCortes = itens.reduce((s, i) => s + i.qtd, 0);
    const totalSolicitado = this.calcularTotalMetros(itens);
    const sobraTotal = this.arredondar2(
      this.calcularSobra(
        totalSolicitado,
        produto?.comprimentoProduto,
        produto?.permiteFracionar
      )
    );
    const totalFinal = this.arredondar2(totalSolicitado + sobraTotal);
    const pesoTotal = this.calcularPeso(totalFinal, produto?.fatorConversao);
    return this.normalizarResumo({
      totalCortes,
      totalSolicitado,
      pesoTotal,
      sobraTotal,
      totalFinal,
    });
  }

  private normalizarResumo(resumo: ResumoCorte): ResumoCorte {
    return {
      totalCortes: resumo.totalCortes,
      totalSolicitado: this.arredondar2(resumo.totalSolicitado),
      pesoTotal: this.arredondar2(resumo.pesoTotal),
      sobraTotal: this.arredondar2(resumo.sobraTotal),
      totalFinal: this.arredondar2(resumo.totalFinal),
      liquidoElegivel:
        resumo.liquidoElegivel != null
          ? this.arredondar2(resumo.liquidoElegivel)
          : undefined,
      liquidoNaoElegivel:
        resumo.liquidoNaoElegivel != null
          ? this.arredondar2(resumo.liquidoNaoElegivel)
          : undefined,
      sobraLiquida:
        resumo.sobraLiquida != null ? this.arredondar2(resumo.sobraLiquida) : undefined,
      devolvido: resumo.devolvido != null ? this.arredondar2(resumo.devolvido) : undefined,
      pesoTotalLiquido:
        resumo.pesoTotalLiquido != null
          ? this.arredondar3(resumo.pesoTotalLiquido)
          : undefined,
      precoUnitario:
        resumo.precoUnitario != null ? this.arredondar2(resumo.precoUnitario) : undefined,
      valorTotal:
        resumo.valorTotal != null ? this.arredondar2(resumo.valorTotal) : undefined,
    };
  }

  /** Valor total do serviço de corte (`valor_servico_corte`). */
  extrairValorServicoCorte(
    produto: any,
    bloco?: CorteBlocoApi | null
  ): number {
    const b = bloco ?? this.extrairBlocoCorte(produto);
    const v = Number(
      b?.valor_servico_corte ??
        produto?.valor_servico_corte ??
        produto?.valorServicoCorte ??
        produto?.corte?.valor_servico_corte ??
        0
    );
    return this.arredondar2(Number.isFinite(v) && v >= 0 ? v : 0);
  }

  /** Preço do serviço de corte (`preco_servico_corte`). */
  extrairPrecoServicoCorte(
    produto: any,
    bloco?: CorteBlocoApi | null,
    fallback = 0
  ): number {
    const b = bloco ?? this.extrairBlocoCorte(produto);
    const v = Number(
      b?.preco_servico_corte ??
        produto?.preco_servico_corte ??
        produto?.precoServicoCorte ??
        produto?.corte?.preco_servico_corte ??
        fallback
    );
    return this.arredondar2(Number.isFinite(v) && v >= 0 ? v : fallback);
  }

  /** Preço unitário do produto (`preco_unitario` da API de corte). */
  extrairPrecoUnitarioCorte(
    produto: any,
    bloco?: CorteBlocoApi | null,
    fallback = 0
  ): number {
    const b = bloco ?? this.extrairBlocoCorte(produto);
    const v = Number(
      b?.preco_unitario ??
        produto?.preco_unitario ??
        produto?.precoUnitario ??
        produto?.corte?.preco_unitario ??
        fallback
    );
    return this.arredondar2(Number.isFinite(v) && v >= 0 ? v : fallback);
  }

  /** Peso líquido (`peso_total_liquido` da API de corte). */
  extrairPesoTotalLiquido(
    produto: any,
    bloco?: CorteBlocoApi | null,
    fallback = 0
  ): number {
    const b = bloco ?? this.extrairBlocoCorte(produto);
    const v = Number(
      b?.peso_total_liquido ??
        produto?.peso_total_liquido ??
        produto?.pesoTotalLiquido ??
        produto?.corte?.peso_total_liquido ??
        fallback
    );
    return this.arredondar3(Number.isFinite(v) && v >= 0 ? v : fallback);
  }

  /** Valor total do corte (`valor_total` da API ou `preco_unitario × peso_total_liquido`). */
  calcularValorTotalCorte(
    produto: any,
    bloco?: CorteBlocoApi | null,
    fallbackPreco = 0,
    fallbackPeso = 0
  ): number {
    const b = bloco ?? this.extrairBlocoCorte(produto);
    const valorApi = Number(b?.valor_total ?? produto?.valor_total ?? produto?.corte?.valor_total);
    if (Number.isFinite(valorApi) && valorApi >= 0) {
      return this.arredondar2(valorApi);
    }
    const preco = this.extrairPrecoUnitarioCorte(produto, bloco, fallbackPreco);
    const peso = this.extrairPesoTotalLiquido(produto, bloco, fallbackPeso);
    if (preco <= 0 || peso <= 0) {
      return 0;
    }
    return this.arredondar2(preco * peso);
  }

  /** Lê o bloco `corte` do payload do produto. */
  extrairBlocoCorte(produto: any): CorteBlocoApi | null {
    const bloco = produto?.corte ?? produto?.Corte ?? null;
    if (!bloco || typeof bloco !== "object") {
      return null;
    }
    return bloco as CorteBlocoApi;
  }

  /** Converte `detalhe` (POST v2/pedido/itens) em bloco `corte`. */
  detalheParaBlocoCorte(detalhe: any): CorteBlocoApi | null {
    if (!detalhe || typeof detalhe !== "object") {
      return null;
    }
    const nested = detalhe.corte ?? detalhe.Corte;
    if (nested && typeof nested === "object") {
      return nested as CorteBlocoApi;
    }
    const temBarras = Array.isArray(detalhe.barras) && detalhe.barras.length > 0;
    const temCortes = Array.isArray(detalhe.cortes) && detalhe.cortes.length > 0;
    const temTotais =
      detalhe.total_sobra != null ||
      detalhe.total_comprado != null ||
      detalhe.liquido_elegivel != null ||
      detalhe.liquido_nao_elegivel != null ||
      detalhe.sobra_liquida != null ||
      detalhe.total_elegivel != null ||
      detalhe.total_nao_elegivel != null ||
      detalhe.devolvido != null ||
      detalhe.devolvido_nao_elegivel != null ||
      detalhe.valor_servico_corte != null ||
      detalhe.preco_servico_corte != null ||
      detalhe.preco_unitario != null ||
      detalhe.peso_total_liquido != null ||
      detalhe.valor_total != null ||
      temBarras ||
      temCortes;
    if (!temTotais) {
      return null;
    }
    return {
      barras: detalhe.barras,
      cortes: detalhe.cortes,
      valor_servico_corte: detalhe.valor_servico_corte,
      preco_servico_corte: detalhe.preco_servico_corte,
      preco_unitario: detalhe.preco_unitario,
      peso_total_liquido: detalhe.peso_total_liquido,
      valor_total: detalhe.valor_total,
      metragem_digitada: detalhe.metragem_digitada,
      total_sobra: detalhe.total_sobra,
      total_comprado: detalhe.total_comprado,
      liquido_elegivel: detalhe.liquido_elegivel ?? detalhe.total_elegivel,
      liquido_nao_elegivel: detalhe.liquido_nao_elegivel ?? detalhe.total_nao_elegivel,
      sobra_liquida: detalhe.sobra_liquida,
      devolvido: detalhe.devolvido,
      devolvido_nao_elegivel: detalhe.devolvido_nao_elegivel,
      tipo_cobranca: detalhe.tipo_cobranca,
    };
  }

  /** Mescla bloco de corte anterior com métricas novas da API (POST sync). */
  mesclarBlocoCorteApi(
    anterior: CorteBlocoApi | null | undefined,
    novo: CorteBlocoApi | null | undefined
  ): CorteBlocoApi | null {
    if (!novo && !anterior) {
      return null;
    }
    if (!novo) {
      return anterior ?? null;
    }
    if (!anterior) {
      return { ...novo };
    }
    const pick = <T>(n: T | null | undefined, a: T | null | undefined): T | undefined =>
      n != null ? n : a != null ? a : undefined;

    return {
      barras: novo.barras?.length ? novo.barras.map((b) => ({ ...b })) : anterior.barras,
      cortes: novo.cortes?.length ? novo.cortes : anterior.cortes,
      total_sobra: pick(novo.total_sobra, anterior.total_sobra),
      total_comprado: pick(novo.total_comprado, anterior.total_comprado),
      liquido_elegivel: pick(novo.liquido_elegivel ?? novo.total_elegivel, anterior.liquido_elegivel),
      liquido_nao_elegivel: pick(
        novo.liquido_nao_elegivel ?? novo.total_nao_elegivel,
        anterior.liquido_nao_elegivel
      ),
      sobra_liquida: pick(novo.sobra_liquida, anterior.sobra_liquida),
      valor_servico_corte: pick(novo.valor_servico_corte, anterior.valor_servico_corte),
      preco_servico_corte: pick(novo.preco_servico_corte, anterior.preco_servico_corte),
      preco_unitario: pick(novo.preco_unitario, anterior.preco_unitario),
      peso_total_liquido: pick(novo.peso_total_liquido, anterior.peso_total_liquido),
      valor_total: pick(novo.valor_total, anterior.valor_total),
      metragem_digitada: pick(novo.metragem_digitada, anterior.metragem_digitada),
      devolvido: pick(novo.devolvido, anterior.devolvido),
      devolvido_nao_elegivel: pick(
        novo.devolvido_nao_elegivel,
        anterior.devolvido_nao_elegivel
      ),
      tipo_cobranca: pick(novo.tipo_cobranca, anterior.tipo_cobranca),
    };
  }

  /**
   * Mapeia seleção do serviço de corte → `tipo_cobranca` da API.
   * `gratis` | `corte` | `kilo`
   */
  mapTipoServicoParaCobranca(tipo?: string | null): string {
    const t = String(tipo ?? "").toLowerCase();
    if (t === "gratis" || t === "abonado") {
      return "gratis";
    }
    if (t === "kilo") {
      return "kilo";
    }
    return "corte";
  }

  /**
   * Mapeia `tipo_cobranca` da API → seleção do serviço de corte na UI.
   * `gratis` | `corte` | `kilo` → `gratis` | `cortes` | `kilo`
   */
  mapCobrancaParaTipoServico(
    cobranca?: string | null
  ): "gratis" | "cortes" | "kilo" {
    const t = String(cobranca ?? "").toLowerCase();
    if (t === "gratis" || t === "abonado") {
      return "gratis";
    }
    if (t === "kilo") {
      return "kilo";
    }
    if (t === "corte" || t === "cortes") {
      return "cortes";
    }
    return "cortes";
  }

  /** Nós onde a API pode embutir `calculos_corte` (inclui `resultados[]`). */
  private nosRespostaSyncPedidoItens(resp: any): any[] {
    const nos: any[] = [];
    if (resp != null && typeof resp === "object") {
      nos.push(resp);
    }
    const resultados = resp?.resultados ?? resp?.Resultados;
    if (Array.isArray(resultados)) {
      for (const r of resultados) {
        if (r != null && typeof r === "object") {
          nos.push(r);
        }
      }
    }
    return nos;
  }

  /**
   * POST sync sem linhas em `calculos_corte` para o cliente/pedido da resposta
   * (ex.: exclusão de todos os cortes do produto).
   */
  respostaSyncIndicaCorteRemovido(
    resp: any,
    ctx?: Omit<CorteCalculoContexto, "produtoId">
  ): boolean {
    const nos = this.nosRespostaSyncPedidoItens(resp);
    for (const no of nos) {
      if (ctx && !this.noCombinaContextoPedido(no, { produtoId: 0, ...ctx })) {
        continue;
      }
      const calc = no?.calculos_corte ?? no?.calculosCorte;
      if (Array.isArray(calc) && calc.length === 0) {
        return true;
      }
    }
    return false;
  }

  private noCombinaContextoPedido(no: any, ctx: CorteCalculoContexto): boolean {
    if (
      ctx.pedidoClienteId != null &&
      no?.pedido_cliente_id != null &&
      Number(no.pedido_cliente_id) !== Number(ctx.pedidoClienteId)
    ) {
      return false;
    }
    if (
      ctx.clienteId != null &&
      no?.cliente_id != null &&
      Number(no.cliente_id) !== Number(ctx.clienteId)
    ) {
      return false;
    }
    return true;
  }

  /**
   * POST v2/pedido/itens → `resultados[].calculos_corte[].calculo` (`total_sobra`, etc.).
   */
  extrairBlocoDeCalculosCorte(
    resp: any,
    ctx: CorteCalculoContexto
  ): CorteBlocoApi | null {
    if (resp == null || !Number.isFinite(ctx.produtoId)) {
      return null;
    }

    const entradas: any[] = [];
    const coletar = (arr: any) => {
      if (Array.isArray(arr)) {
        entradas.push(...arr);
      }
    };

    const coletarDeNo = (no: any) => {
      coletar(no?.calculos_corte);
      coletar(no?.calculosCorte);
      for (const cli of no?.clientes ?? []) {
        coletar(cli?.calculos_corte);
        coletar(cli?.calculosCorte);
      }
      for (const line of no?.itens ?? []) {
        coletar(line?.calculos_corte);
        coletar(line?.calculosCorte);
      }
    };

    const nos = this.nosRespostaSyncPedidoItens(resp);
    for (const no of nos) {
      if (!this.noCombinaContextoPedido(no, ctx)) {
        continue;
      }
      coletarDeNo(no);
    }

    if (!entradas.length) {
      for (const no of nos) {
        coletarDeNo(no);
      }
    }

    if (!entradas.length) {
      return null;
    }

    const combina = (entry: any, estrito: boolean): boolean => {
      const pid = Number(entry?.produto_id ?? entry?.produto ?? 0);
      if (pid !== Number(ctx.produtoId)) {
        return false;
      }
      if (!estrito) {
        return true;
      }
      if (ctx.cor != null && Number(entry?.cor ?? -1) !== Number(ctx.cor)) {
        return false;
      }
      if (
        ctx.estampa != null &&
        Number(entry?.estampa ?? -1) !== Number(ctx.estampa)
      ) {
        return false;
      }
      const tam = String(entry?.tamanho ?? "U").trim();
      const quer = String(ctx.tamanho ?? "U").trim();
      return tam === quer;
    };

    const entrada =
      entradas.find((e) => combina(e, true)) ??
      entradas.find((e) => combina(e, false));

    if (!entrada) {
      return null;
    }

    const calculo = entrada?.calculo ?? entrada?.Calculo;
    return this.detalheParaBlocoCorte(calculo);
  }

  /**
   * Extrai `corte` / `total_sobra` da resposta POST `v2/pedido/itens`
   * (mesmo critério do GET produto ao abrir a tela).
   */
  extrairBlocoCorteDeRespostaSync(
    resp: any,
    produtoId: number,
    ctxParcial?: Omit<CorteCalculoContexto, "produtoId">
  ): CorteBlocoApi | null {
    if (resp == null || !Number.isFinite(produtoId)) {
      return null;
    }

    const id = Number(produtoId);
    const ctx: CorteCalculoContexto = {
      produtoId: id,
      cor: ctxParcial?.cor,
      estampa: ctxParcial?.estampa,
      tamanho: ctxParcial?.tamanho,
    };

    const fromCalculos = this.extrairBlocoDeCalculosCorte(resp, ctx);
    if (fromCalculos) {
      return fromCalculos;
    }

    const fromProduto = (p: any) => {
      if (!p || Number(p?.id ?? p?.produto) !== id) {
        return null;
      }
      return this.extrairBlocoCorte(p);
    };

    const raiz = fromProduto(resp?.produto ?? resp?.product);
    if (raiz) {
      return raiz;
    }

    const blocoRaiz = resp?.corte ?? resp?.Corte;
    if (blocoRaiz && typeof blocoRaiz === "object") {
      return blocoRaiz as CorteBlocoApi;
    }

    const detalheRaiz = this.detalheParaBlocoCorte(resp?.detalhe ?? resp?.Detalhe);
    if (detalheRaiz) {
      return detalheRaiz;
    }

    const produtos = resp?.produtos ?? resp?.products;
    if (Array.isArray(produtos)) {
      for (const p of produtos) {
        const b = fromProduto(p);
        if (b) {
          return b;
        }
      }
    }

    const itens = resp?.itens;
    if (Array.isArray(itens)) {
      for (const line of itens) {
        const pid = Number(line?.produto ?? line?.produto_id ?? 0);
        if (pid !== id) {
          continue;
        }
        const fromLineProduto = fromProduto(line?.produto_detalhe ?? line?.product);
        if (fromLineProduto) {
          return fromLineProduto;
        }
        const fromDetalhe = this.detalheParaBlocoCorte(
          line?.detalhe ?? line?.Detalhe
        );
        if (fromDetalhe) {
          return fromDetalhe;
        }
        const obs =
          line?.observacaoCorte ??
          line?.observacao_corte ??
          line?.observacao ??
          null;
        if (obs) {
          try {
            const parsed = typeof obs === "string" ? JSON.parse(obs) : obs;
            const b = this.detalheParaBlocoCorte(parsed) ?? this.extrairBlocoCorte({
              corte: parsed,
            });
            if (b) {
              return b;
            }
          } catch {
            /* ignore */
          }
        }
      }
    }

    const clientes = resp?.clientes;
    if (Array.isArray(clientes)) {
      for (const cli of clientes) {
        const grade = cli?.grade;
        if (!Array.isArray(grade)) {
          continue;
        }
        for (const g of grade) {
          if (Number(g?.produto ?? 0) !== id) {
            continue;
          }
          const variacoes = g?.variacoes;
          if (!Array.isArray(variacoes)) {
            continue;
          }
          for (const v of variacoes) {
            const tamanhos = v?.tamanhos;
            if (!Array.isArray(tamanhos)) {
              continue;
            }
            for (const t of tamanhos) {
              const b = this.detalheParaBlocoCorte(t?.detalhe ?? t?.Detalhe);
              if (b) {
                return b;
              }
              const respCorte = t?.resposta?.corte ?? t?.corte;
              if (respCorte && typeof respCorte === "object") {
                return respCorte as CorteBlocoApi;
              }
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Comprimento da barra: prioriza `corte.barras[0].tamanho_barra`, depois `comprimento_util` do produto.
   */
  extrairTamanhoBarra(produto: any, bloco?: CorteBlocoApi | null): number | null {
    const b = bloco ?? this.extrairBlocoCorte(produto);
    const barras = b?.barras;
    if (Array.isArray(barras) && barras.length > 0) {
      const t = Number(barras[0]?.tamanho_barra ?? 0);
      if (Number.isFinite(t) && t > 0) {
        return t;
      }
    }
    return this.extrairMedidaIntegral(produto);
  }

  /**
   * Converte `corte.barras[].cortes` (lista de metros por corte) em itens do formulário.
   * Cada valor numérico vira um corte com qtd 1; comprimentos iguais são agrupados depois.
   */
  itensDeBlocoCorte(
    bloco: CorteBlocoApi | null,
    itensAnteriores?: CorteItem[] | null
  ): CorteItem[] {
    if (!bloco) {
      return [];
    }

    const idPorComprimento = (comprimento_util: number): string => {
      const chave = Math.round(comprimento_util * 100);
      const existente = itensAnteriores?.find(
        (it) => Math.round(it.comprimento_util * 100) === chave
      );
      return existente?.id ?? this.novoId();
    };

    const barras = bloco.barras;
    if (Array.isArray(barras) && barras.length > 0) {
      const basicos: CorteBasico[] = [];
      for (const barra of barras) {
        const lista = barra?.cortes;
        if (!Array.isArray(lista)) {
          continue;
        }
        for (const raw of lista) {
          const comprimento_util =
            typeof raw === "number"
              ? Number(raw)
              : Number(
                  (raw as any)?.comprimento_util ??
                    (raw as any)?.comprimento ??
                    (raw as any)?.medida ??
                    0
                );
          if (Number.isFinite(comprimento_util) && comprimento_util > 0) {
            basicos.push({ qtd: 1, comprimento_util });
          }
        }
      }
      if (basicos.length > 0) {
        return this.agrupar(
          basicos.map((b) => ({
            ...b,
            id: idPorComprimento(b.comprimento_util),
          }))
        );
      }
    }

    const cortes = bloco.cortes;
    if (!Array.isArray(cortes) || cortes.length === 0) {
      return [];
    }
    const itens: CorteItem[] = cortes
      .map((c) => {
        const comprimento_util =
          Number(c?.comprimento_util ?? c?.comprimento ?? c?.tamanho ?? 0) || 0;
        return {
          id: idPorComprimento(comprimento_util),
          qtd: Number(c?.quantidade ?? c?.qtd ?? 0) || 0,
          comprimento_util,
        };
      })
      .filter((x) => x.qtd > 0 && x.comprimento_util > 0);
    return this.agrupar(itens);
  }

  /**
   * Comprimentos individuais em `barras[].cortes` (POST/GET corte).
   */
  extrairComprimentosCortesBloco(bloco: CorteBlocoApi | null | undefined): number[] {
    if (!bloco) {
      return [];
    }
    const valores: number[] = [];
    const barras = Array.isArray(bloco.barras) ? bloco.barras : [];
    for (const barra of barras) {
      const lista = barra?.cortes;
      if (!Array.isArray(lista)) {
        continue;
      }
      for (const raw of lista) {
        const comp =
          typeof raw === "number"
            ? Number(raw)
            : Number(
                (raw as any)?.comprimento_util ??
                  (raw as any)?.comprimento ??
                  (raw as any)?.medida ??
                  0
              );
        if (Number.isFinite(comp) && comp > 0) {
          valores.push(comp);
        }
      }
    }
    return valores;
  }

  /**
   * Mapa medida de corte → metragem elegível para devolução (`medidas_elegiveis_corte`).
   */
  private mapaMedidasElegiveisDevolucao(produto: any): Map<number, number> {
    const arr = produto?.medidas_elegiveis_corte ?? produto?.medidasElegiveisCorte ?? [];
    const map = new Map<number, number>();
    if (!Array.isArray(arr)) {
      return map;
    }
    for (const m of arr) {
      if (m?.permitido === false) {
        continue;
      }
      const medida = Number(m?.medida ?? 0);
      const devolucao = Number(m?.medida_elegivel_devolucao ?? m?.medidaElegivelDevolucao ?? 0);
      if (Number.isFinite(medida) && medida > 0 && Number.isFinite(devolucao) && devolucao > 0) {
        map.set(Math.round(medida * 100), devolucao);
      }
    }
    return map;
  }

  /**
   * Lista de metragens elegíveis para devolução, uma entrada por corte do array `cortes`.
   */
  extrairCortesElegiveisDevolucao(
    bloco: CorteBlocoApi | null | undefined,
    produto?: any | null
  ): number[] {
    const mapa = this.mapaMedidasElegiveisDevolucao(produto);
    const cortes = this.extrairComprimentosCortesBloco(bloco);
    const elegiveis: number[] = [];

    for (const comp of cortes) {
      const devolucao = mapa.get(Math.round(comp * 100));
      if (devolucao != null && devolucao > 0) {
        elegiveis.push(devolucao);
      }
    }

    if (elegiveis.length === 0 && bloco && mapa.size > 0) {
      const sobraElegivel = Number(
        bloco.liquido_elegivel ?? bloco.total_elegivel ?? 0
      );
      if (Number.isFinite(sobraElegivel) && sobraElegivel > 0) {
        const unidade = Array.from(mapa.values()).sort((a, b) => a - b)[0];
        if (unidade > 0) {
          const qtd = Math.max(1, Math.round(sobraElegivel / unidade));
          for (let i = 0; i < qtd; i++) {
            elegiveis.push(unidade);
          }
        }
      }
    }

    return elegiveis;
  }

  /**
   * Múltiplo de devolução por barra (`sobra_elegivel` ou `sobra_nao_elegivel`).
   */
  private extrairMultiploDevolucaoBarra(
    barras: CorteBarraApi[],
    campo: "sobra_elegivel" | "sobra_nao_elegivel"
  ): number {
    for (const barra of barras) {
      const v = Number(barra?.[campo] ?? 0);
      if (Number.isFinite(v) && v > 0) {
        return v;
      }
    }
    return 0;
  }

  /** Quantidade máxima de unidades (total ÷ múltiplo). */
  maxUnidadesDevolucao(total: number, multiplo: number): number {
    const t = Number(total);
    const m = Number(multiplo);
    if (!Number.isFinite(t) || t <= 0 || !Number.isFinite(m) || m <= 0) {
      return 0;
    }
    const qtd = Math.round((t / m) * 1000) / 1000;
    const unidades = Math.floor(qtd + 1e-6);
    return unidades > 0 ? unidades : 0;
  }

  /**
   * Extrai totais de sobra/devolução do bloco `corte` (nível raiz ou soma das barras).
   */
  extrairDadosDevolucao(
    bloco: CorteBlocoApi | null | undefined,
    _produto?: any | null
  ): CorteDevolucaoDados {
    if (!bloco) {
      return {
        sobraTotal: 0,
        sobraLiquida: 0,
        sobraElegivel: 0,
        sobraNaoElegivel: 0,
        devolvido: 0,
        devolvidoNaoElegivel: 0,
        multiploElegivel: 0,
        multiploNaoElegivel: 0,
      };
    }

    const barras = Array.isArray(bloco.barras) ? bloco.barras : [];
    const somaBarras = (campo: keyof CorteBarraApi) =>
      this.arredondar2(
        barras.reduce((s, b) => {
          const v = Number(b[campo] ?? 0);
          return s + (Number.isFinite(v) ? v : 0);
        }, 0)
      );

    const pick = (raiz: number | undefined, campoBarra: keyof CorteBarraApi): number => {
      const top = Number(raiz);
      if (Number.isFinite(top) && top >= 0) {
        return this.arredondar2(top);
      }
      if (barras.length > 0) {
        return somaBarras(campoBarra);
      }
      return 0;
    };

    const devolvido = this.arredondar2(
      Number.isFinite(Number(bloco.devolvido)) && Number(bloco.devolvido) >= 0
        ? Number(bloco.devolvido)
        : 0
    );
    const devolvidoNaoElegivel = this.arredondar2(
      Number.isFinite(Number(bloco.devolvido_nao_elegivel)) &&
        Number(bloco.devolvido_nao_elegivel) >= 0
        ? Number(bloco.devolvido_nao_elegivel)
        : 0
    );

    const sobraTotal = pick(bloco.total_sobra, "sobra_total");

    const liquidoElegivel = (): number => {
      const raiz = bloco.liquido_elegivel ?? bloco.total_elegivel;
      const top = Number(raiz);
      if (Number.isFinite(top) && top >= 0) {
        return this.arredondar2(top);
      }
      if (barras.length > 0) {
        return this.arredondar2(Math.max(0, somaBarras("sobra_elegivel") - devolvido));
      }
      return 0;
    };

    const liquidoNaoElegivel = (): number => {
      const raiz = bloco.liquido_nao_elegivel ?? bloco.total_nao_elegivel;
      const top = Number(raiz);
      if (Number.isFinite(top) && top >= 0) {
        return this.arredondar2(top);
      }
      if (barras.length > 0) {
        return this.arredondar2(
          Math.max(0, somaBarras("sobra_nao_elegivel") - devolvidoNaoElegivel)
        );
      }
      return 0;
    };

    const sobraLiquida = (): number => {
      const api = Number(bloco.sobra_liquida);
      if (Number.isFinite(api) && api >= 0) {
        return this.arredondar2(api);
      }
      return this.arredondar2(
        Math.max(0, sobraTotal - devolvido - devolvidoNaoElegivel)
      );
    };

    return {
      sobraTotal,
      sobraLiquida: sobraLiquida(),
      sobraElegivel: liquidoElegivel(),
      sobraNaoElegivel: liquidoNaoElegivel(),
      devolvido,
      devolvidoNaoElegivel,
      multiploElegivel: this.arredondar2(
        this.extrairMultiploDevolucaoBarra(barras, "sobra_elegivel")
      ),
      multiploNaoElegivel: this.arredondar2(
        this.extrairMultiploDevolucaoBarra(barras, "sobra_nao_elegivel")
      ),
    };
  }

  /** Aplica `total_sobra` e demais totais do GET no resumo exibido. */
  aplicarResumoCorteApi(
    resumo: ResumoCorte,
    bloco: CorteBlocoApi | null | undefined,
    produto?: ProdutoCorteInfo | null
  ): ResumoCorte {
    if (!bloco) {
      return resumo;
    }

    const dev = this.extrairDadosDevolucao(bloco);
    const temTotalRaiz = Number.isFinite(Number(bloco.total_sobra));
    const temSobraBarras =
      Array.isArray(bloco.barras) &&
      bloco.barras.some((b) => Number.isFinite(Number(b?.sobra_total)));
    const usaSobraApi = temTotalRaiz || temSobraBarras;

    const precoUnitario = this.extrairPrecoUnitarioCorte(
      produto as any,
      bloco,
      resumo.precoUnitario ?? 0
    );
    const pesoApi = Number(bloco.peso_total_liquido);
    const temPesoApi = Number.isFinite(pesoApi) && pesoApi >= 0;
    const pesoLiquido = temPesoApi
      ? this.arredondar3(pesoApi)
      : this.extrairPesoTotalLiquido(produto as any, bloco, resumo.pesoTotal ?? 0);
    const valorApi = Number(bloco.valor_total);
    const valorTotal =
      Number.isFinite(valorApi) && valorApi >= 0
        ? this.arredondar2(valorApi)
        : this.calcularValorTotalCorte(
            produto as any,
            bloco,
            precoUnitario,
            pesoLiquido
          );

    const next: ResumoCorte = {
      ...resumo,
      liquidoElegivel: dev.sobraElegivel,
      liquidoNaoElegivel: dev.sobraNaoElegivel,
      sobraLiquida: dev.sobraLiquida,
      devolvido: dev.devolvido,
      precoUnitario: precoUnitario > 0 ? precoUnitario : undefined,
      pesoTotalLiquido: pesoLiquido > 0 ? pesoLiquido : undefined,
      valorTotal: valorTotal > 0 ? valorTotal : undefined,
    };

    if (usaSobraApi) {
      next.sobraTotal = dev.sobraTotal;
      const totalComprado = Number(bloco.total_comprado);
      if (Number.isFinite(totalComprado) && totalComprado >= 0) {
        next.totalSolicitado = this.arredondar2(totalComprado);
      }
      next.totalFinal = this.arredondar2(next.totalSolicitado + dev.sobraLiquida);
      next.pesoTotal = temPesoApi
        ? pesoLiquido
        : pesoLiquido > 0
          ? pesoLiquido
          : this.calcularPeso(next.totalFinal, produto?.fatorConversao);
    } else if (temPesoApi || pesoLiquido > 0) {
      next.pesoTotal = pesoLiquido;
    }

    return this.normalizarResumo(next);
  }

  /** @deprecated Use aplicarResumoCorteApi */
  enriquecerResumoComBloco(
    resumo: ResumoCorte,
    bloco: CorteBlocoApi | null | undefined,
    produto?: ProdutoCorteInfo | null
  ): ResumoCorte {
    return this.aplicarResumoCorteApi(resumo, bloco, produto);
  }

  /** Monta `barras` para observação/sync a partir dos itens do formulário. */
  montarBarrasDeItens(
    itens: CorteBasico[],
    tamanhoBarra: number | null | undefined
  ): CorteBarraApi[] {
    const cortes: number[] = [];
    for (const it of itens) {
      const qtd = Math.max(0, Math.floor(Number(it.qtd) || 0));
      const comp = Number(it.comprimento_util) || 0;
      if (comp <= 0) {
        continue;
      }
      for (let i = 0; i < qtd; i++) {
        cortes.push(comp);
      }
    }
    if (!cortes.length) {
      return [];
    }
    const tam = Number(tamanhoBarra ?? 0);
    return [
      {
        numero: 1,
        cortes,
        tamanho_barra: Number.isFinite(tam) && tam > 0 ? tam : undefined,
        status: "OPERADOR_DECIDE",
      },
    ];
  }

  /**
   * Monta `detalhe` mínimo para POST `v2/pedido/itens` (modo sync).
   * O backend recalcula barras, totais e `calculo` — não reenviar eco do GET.
   * Retorna `null` se não houver linha de corte válida (qtd e comprimento_util > 0).
   */
  montarDetalheCorteSync(
    itens: CorteBasico[],
    bloco: CorteBlocoApi | null | undefined,
    _tamanhoBarra: number | null | undefined,
    opcoes?: CorteDetalheSyncOpcoes
  ): Record<string, unknown> | null {
    const cortes = itens
      .map((it) => ({
        quantidade: Math.max(0, Math.floor(Number(it.qtd) || 0)),
        comprimento_util: Number(it.comprimento_util) || 0,
      }))
      .filter((c) => c.quantidade > 0 && c.comprimento_util > 0);

    if (!cortes.length) {
      return null;
    }

    const devolvidoValor =
      opcoes?.devolvido != null && Number.isFinite(opcoes.devolvido) && opcoes.devolvido >= 0
        ? opcoes.devolvido
        : Number(bloco?.devolvido ?? 0);

    const devolvidoNaoElegivelValor =
      opcoes?.devolvido_nao_elegivel != null &&
      Number.isFinite(opcoes.devolvido_nao_elegivel) &&
      opcoes.devolvido_nao_elegivel >= 0
        ? opcoes.devolvido_nao_elegivel
        : Number(bloco?.devolvido_nao_elegivel ?? 0);

    const tipoCobranca =
      opcoes?.tipo_cobranca ??
      bloco?.tipo_cobranca ??
      this.mapTipoServicoParaCobranca("cortes");

    return {
      tipo: "corte",
      cortes,
      devolvido: devolvidoValor,
      devolvido_nao_elegivel: devolvidoNaoElegivelValor,
      tipo_cobranca: tipoCobranca,
    };
  }

  observacaoCortePayload(
    itens: CorteBasico[],
    resumo: ResumoCorte,
    tamanhoBarra: number | null | undefined
  ): Record<string, unknown> {
    const barras = this.montarBarrasDeItens(itens, tamanhoBarra);
    return {
      cortes: itens.map(({ qtd, comprimento_util }) => ({
        qtd,
        comprimento_util,
      })),
      barras,
      totalSolicitado: resumo.totalSolicitado,
      totalCortes: resumo.totalCortes,
      pesoTotal: resumo.pesoTotal,
      sobraTotal: resumo.sobraTotal,
      totalFinal: resumo.totalFinal,
      total_comprado: resumo.totalSolicitado,
      total_sobra: resumo.sobraTotal,
      sobra_liquida: resumo.sobraLiquida ?? resumo.sobraTotal,
      liquido_elegivel: resumo.liquidoElegivel ?? 0,
      liquido_nao_elegivel: resumo.liquidoNaoElegivel ?? 0,
      devolvido: resumo.devolvido ?? 0,
      peso_total_liquido: resumo.pesoTotalLiquido ?? resumo.pesoTotal,
      preco_unitario: resumo.precoUnitario,
      valor_total: resumo.valorTotal,
    };
  }

  /**
   * Monta linhas da tabela a partir de `corte.barras[]` (com sobra na última linha de cada barra)
   * ou, sem barras, a partir dos itens agrupados do formulário.
   */
  linhasGridCorte(
    bloco: CorteBlocoApi | null | undefined,
    itens: CorteItem[]
  ): CorteLinhaGrid[] {
    const barras = bloco?.barras;
    if (!Array.isArray(barras) || barras.length === 0) {
      return this.linhasGridDeItens(itens);
    }

    const itensVinculo =
      Array.isArray(itens) && itens.length > 0
        ? itens
        : this.itensDeBlocoCorte(bloco ?? null);

    const linhas: CorteLinhaGrid[] = [];
    for (let bi = 0; bi < barras.length; bi++) {
      const barra = barras[bi];
      const numeroBarra = Number(barra?.numero ?? bi + 1);
      const cortesLista = Array.isArray(barra?.cortes) ? barra.cortes : [];
      const grupos = this.agruparComprimentosListaCortes(cortesLista);
      const entries = Array.from(grupos.entries()).sort((a, b) => a[0] - b[0]);
      const sobraRaw = Number(barra?.sobra_total);
      const sobraBarra = Number.isFinite(sobraRaw) ? sobraRaw : null;
      const rowspan = Math.max(entries.length, 1);

      if (entries.length === 0) {
        continue;
      }

      entries.forEach(([chave, qtd], idx) => {
        const comprimento_util = chave / 100;
        const item = this.encontrarItemPorComprimento(itensVinculo, comprimento_util);
        const ultima = idx === entries.length - 1;
        linhas.push({
          barra: numeroBarra,
          qtd,
          comprimento_util,
          sobra: ultima ? sobraBarra : null,
          itemId: item?.id ?? null,
          flash: item?.flash,
          exibirCelulaBarra: idx === 0,
          rowspanBarra: rowspan,
          fimGrupoBarra: ultima,
        });
      });
    }

    return linhas.length > 0 ? linhas : this.linhasGridDeItens(itensVinculo);
  }

  /** Localiza o item do formulário correspondente a uma linha da tabela. */
  encontrarItemParaLinhaGrid(
    lin: Pick<CorteLinhaGrid, "itemId" | "comprimento_util">,
    itens: CorteItem[]
  ): CorteItem | undefined {
    if (!Array.isArray(itens) || itens.length === 0) {
      return undefined;
    }
    if (lin.itemId) {
      const porId = itens.find((it) => it.id === lin.itemId);
      if (porId) {
        return porId;
      }
    }
    return this.encontrarItemPorComprimento(itens, lin.comprimento_util);
  }

  /** @deprecated Use encontrarItemParaLinhaGrid */
  resolverItemIdLinha(
    lin: Pick<CorteLinhaGrid, "itemId" | "comprimento_util">,
    itens: CorteItem[],
    bloco?: CorteBlocoApi | null
  ): string | null {
    const lista =
      Array.isArray(itens) && itens.length > 0
        ? itens
        : this.itensDeBlocoCorte(bloco ?? null);
    return this.encontrarItemParaLinhaGrid(lin, lista)?.id ?? null;
  }

  private linhasGridDeItens(itens: CorteItem[]): CorteLinhaGrid[] {
    return itens.map((it) => ({
      barra: null,
      qtd: it.qtd,
      comprimento_util: it.comprimento_util,
      sobra: null,
      itemId: it.id,
      flash: it.flash,
      exibirCelulaBarra: false,
      rowspanBarra: 1,
      fimGrupoBarra: false,
    }));
  }

  private agruparComprimentosListaCortes(
    cortes: Array<number | Record<string, unknown>>
  ): Map<number, number> {
    const grupos = new Map<number, number>();
    for (const raw of cortes) {
      const comprimento_util =
        typeof raw === "number"
          ? Number(raw)
          : Number(
              (raw as any)?.comprimento_util ??
                (raw as any)?.comprimento ??
                (raw as any)?.medida ??
                0
            );
      if (!Number.isFinite(comprimento_util) || comprimento_util <= 0) {
        continue;
      }
      const chave = Math.round(comprimento_util * 100);
      grupos.set(chave, (grupos.get(chave) ?? 0) + 1);
    }
    return grupos;
  }

  private encontrarItemPorComprimento(
    itens: CorteItem[],
    comprimento_util: number
  ): CorteItem | undefined {
    const chave = Math.round(comprimento_util * 100);
    return itens.find((it) => Math.round(it.comprimento_util * 100) === chave);
  }

  /** Agrupa cortes com mesmo comprimento somando quantidades. */
  agrupar(itens: CorteItem[]): CorteItem[] {
    const map = new Map<number, CorteItem>();
    for (const it of itens) {
      const chave = Math.round(it.comprimento_util * 100);
      const existente = map.get(chave);
      if (existente) {
        existente.qtd += it.qtd;
      } else {
        map.set(chave, { ...it });
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => a.comprimento_util - b.comprimento_util
    );
  }

  /** Medidas permitidas para venda fracionada (GET produto). */
  extrairMedidasFracionado(p: any): number[] {
    const arr = p?.medidas_elegiveis_corte ?? p?.medidasElegiveisCorte ?? [];
    if (!Array.isArray(arr)) {
      return [];
    }
    return arr
      .filter((m: any) => m?.permitido !== false)
      .map((m: any) => Number(m?.medida ?? 0))
      .filter((v: number) => Number.isFinite(v) && v > 0)
      .sort((a, b) => a - b);
  }

  /** Comprimento da barra inteira (venda integral). */
  extrairMedidaIntegral(p: any): number | null {
    const v = Number(p?.comprimento_util ?? p?.comprimentoUtil ?? 0);
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  calcularEstoqueMetros(produto?: ProdutoCorteInfo | null): number {
    const estoqueKg = Number(produto?.estoqueKg ?? 0);
    const uc = Number(produto?.unidadeConversao ?? 0);
    if (!Number.isFinite(estoqueKg) || !Number.isFinite(uc) || uc <= 0) {
      return 0;
    }
    return estoqueKg / uc;
  }

  calcularUtilizacaoEstoquePercentual(
    estoqueMetros: number,
    metrosUtilizados: number
  ): number {
    if (!Number.isFinite(estoqueMetros) || estoqueMetros <= 0) {
      return 0;
    }
    if (!Number.isFinite(metrosUtilizados) || metrosUtilizados <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((metrosUtilizados / estoqueMetros) * 100));
  }

  produtoEhModoCorte(p: any): boolean {
    if (!p) {
      return false;
    }
    const m = p.modo_venda != null ? String(p.modo_venda).toLowerCase() : "";
    const t = p.tipo_venda != null ? String(p.tipo_venda).toLowerCase() : "";
    return m === "corte" || t === "corte";
  }

  /**
   * Abre a página de corte quando o modo é explícito, a API marca permite_corte
   * (mesma regra do carrinho após findById) ou quando a URL força ?corte=1.
   */
  deveUsarPaginaCorte(p: any, forcarViaQueryParam = false): boolean {
    if (forcarViaQueryParam) {
      return true;
    }
    if (this.produtoEhModoCorte(p)) {
      return true;
    }
    return Boolean(p?.permite_corte ?? p?.permiteCorte);
  }
}

